import React from 'react';
import { H5 } from '../../../../AbstractElements';
import { FormGroup, Input, Label, FormFeedback, Row, Col, InputGroup, Button } from 'reactstrap';
import { Search } from 'react-feather';
import PropTypes from 'prop-types';
import FilesComponent from '../Component/FilesComponent';

const CustomerInfoForm = ({ callFormState, formData, errors, handleInputChange, handleCustomerSearch, customerId, fieldPermissions = [], canEdit = true }) => {
    const isSearchDisabled = (idType) => {
        if (idType === 'tcid') {
            return !formData.tcid || formData.tcid.length !== 11;
        }
        if (idType === 'taxNumber') {
            return !formData.taxNumber || formData.taxNumber.length !== 10;
        }
        return true;
    };    // Helper function to get field access properties
    const getFieldAccess = (fieldName) => {
        // If canEdit is false, all fields should be disabled regardless of permissions
        if (!canEdit) {
            return {
                editable: false,
                required: false,
            };
        }
        
        const permission = fieldPermissions.find(p => p.fieldName === fieldName);
        return {
            editable: permission ? permission.access === 'Editable' : true, // Default to editable if not specified
            required: permission ? permission.required : false, // Default to not required
        };
    };

    const agentAccess = getFieldAccess('customers.agent'); // Example mapping
    const createdAccess = getFieldAccess('customers.created');
    const customerTypeAccess = getFieldAccess('customers.customertype'); // Controls the whole section visibility
    const clientNameAccess = getFieldAccess('customers.clientname');
    const tcidAccess = getFieldAccess('customers.identitynumber'); // Map form field to API field name
    const companyNameAccess = getFieldAccess('customers.companyname');
    const crsNumberAccess = getFieldAccess('customers.crsnumber');
    const taxNumberAccess = getFieldAccess('customers.taxnumber');
    const referenceAccess = getFieldAccess('customers.reference'); // API'den gelen field name
    const licensePlateAccess = getFieldAccess('licenseplates.licenseplatenumber');
    const accidentDateAccess = getFieldAccess('accidents.accidentdate');

    return (
        <>
            <H5>Müvekkil Bilgileri</H5>
            <div style={{ height: "20px" }}></div>

            <Row>
                <Col md={6}>
                    <FormGroup>
                        <Label for="recipientOfPowerOfAttorney">
                            Vekaleti Alan {agentAccess.required && <span className="text-danger">*</span>}
                        </Label>
                        <Input
                            type="text"
                            id="recipientOfPowerOfAttorney"
                            name="recipientOfPowerOfAttorney"
                            value={formData.customers?.[0]?.agent 
                                ? `${formData.customers[0].agent.firstName || ''} ${formData.customers[0].agent.lastName || ''}`.trim()
                                : formData.recipientOfPowerOfAttorney || ''}
                            placeholder="Vekaleti alan otomatik olarak atanıyor."
                            disabled={true}
                            invalid={!!errors.recipientOfPowerOfAttorney}
                        />
                        <FormFeedback>
                            {errors.recipientOfPowerOfAttorney}
                        </FormFeedback>
                    </FormGroup>
                </Col>
                <Col md={6}>
                    <FormGroup>
                        <Label for="created">
                            Kayıt Tarihi {createdAccess.required && <span className="text-danger">*</span>}
                        </Label>
                        <Input
                            type="datetime-local"
                            id="created"
                            name="created"
                            value={formData.customers?.[0]?.created instanceof Date 
                                ? formData.customers[0].created.toISOString().slice(0, 16) 
                                : formData.customers?.[0]?.created 
                                    ? new Date(formData.customers[0].created).toISOString().slice(0, 16)
                                    : ''}
                            disabled={true}
                            placeholder="Müşteri kayıt tarihi otomatik olarak atanır"
                            invalid={!!errors.created}
                        />
                        <FormFeedback>{errors.created}</FormFeedback>
                    </FormGroup>
                </Col>
            </Row>

            {/* Conditional Rendering: Bireysel Fields */}
            {formData.customerType === 'Bireysel' && (
                <Row>
                    <Col md={6}>
                        <FormGroup>
                            <Label for="clientName">
                                Müvekkil Adı {clientNameAccess.required && <span className="text-danger">*</span>}
                            </Label>
                            <Input
                                type="text"
                                id="clientName"
                                name="clientName"
                                value={formData.clientName || ''}
                                disabled={!clientNameAccess.editable}
                                onChange={(e) => {
                                    const value = e.target.value
                                        .replace(/[^a-zA-ZğüşıöçĞÜŞİÖÇ\s]/g, '')
                                        .replace(/\s\s+/g, ' ')
                                        .toUpperCase();
                                    handleInputChange({
                                        target: {
                                            name: 'clientName',
                                            value: value || null
                                        }
                                    });
                                }}
                                onBlur={(e) => {
                                    const finalValue = e.target.value.trim();
                                    if (finalValue !== formData.clientName) {
                                        handleInputChange({
                                            target: {
                                                name: 'clientName',
                                                value: finalValue || null
                                            }
                                        });
                                    }
                                }}
                                placeholder="AD SOYAD"
                                invalid={!!errors.clientName || (formData.clientName && !/^[A-ZĞÜŞİÖÇ]+(?: [A-ZĞÜŞİÖÇ]+)+$/.test(formData.clientName.trim()))}
                            />
                            <FormFeedback>
                                {errors.clientName || 'Müvekkil adı tamamen büyük harflerden oluşmalı ve en az iki kelime içermelidir'}
                            </FormFeedback>
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup>
                            <Label for="tcid">
                                TC Kimlik Numarası {tcidAccess.required && <span className="text-danger">*</span>}
                            </Label>
                            <InputGroup>
                                <Input
                                    type="text"
                                    id="tcid"
                                    name="tcid"
                                    value={formData.tcid || ''}
                                    disabled={!tcidAccess.editable}
                                    onChange={(e) => {
                                        const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 11);
                                        handleInputChange({
                                            target: {
                                                name: 'tcid',
                                                value: value || null
                                            }
                                        });
                                    }}
                                    maxLength={11}
                                    pattern="[0-9]{11}"
                                    invalid={!!errors.tcid || (formData.tcid && formData.tcid.length !== 11)}
                                    placeholder="11 Haneli TC Kimlik No"
                                />
                                <Button
                                    color="primary"
                                    outline
                                    onClick={() => handleCustomerSearch('tcid', formData.tcid)}
                                    disabled={!tcidAccess.editable || isSearchDisabled('tcid')}
                                    aria-label="TC Kimlik Numarası ile Müşteri Ara"
                                >
                                    <Search size={16} />
                                </Button>
                                <FormFeedback style={{ display: errors.tcid || (formData.tcid && formData.tcid.length !== 11) ? 'block' : 'none' }}>
                                    {errors.tcid || (formData.tcid && formData.tcid.length !== 11 ? 'TC Kimlik Numarası 11 haneli olmalıdır' : '')}
                                </FormFeedback>
                            </InputGroup>
                        </FormGroup>
                    </Col>
                </Row>
            )}

            {/* Conditional Rendering: Kurumsal Fields */}
            {formData.customerType === 'Kurumsal' && (
                <Row>
                    <Col md={4}>
                        <FormGroup>
                            <Label for="companyName">
                                Şirket Adı {companyNameAccess.required && <span className="text-danger">*</span>}
                            </Label>
                            <Input
                                type="text"
                                id="companyName"
                                name="companyName"
                                value={formData.companyName || ''}
                                disabled={!companyNameAccess.editable}
                                onChange={(e) => {
                                    handleInputChange({
                                        target: {
                                            name: 'companyName',
                                            value: e.target.value || null
                                        }
                                    });
                                }}
                                invalid={!!errors.companyName}
                            />
                            <FormFeedback>{errors.companyName}</FormFeedback>
                        </FormGroup>
                    </Col>
                    <Col md={4}>
                        <FormGroup>
                            <Label for="crsNumber">
                                MERSIS Numarası {crsNumberAccess.required && <span className="text-danger">*</span>}
                            </Label>
                            <Input
                                type="text"
                                id="crsNumber"
                                name="crsNumber"
                                value={formData.crsNumber || ''}
                                disabled={!crsNumberAccess.editable}
                                onChange={(e) => {
                                    const value = e.target.value.replace(/[^0-9]/g, '');
                                    handleInputChange({
                                        target: {
                                            name: 'crsNumber',
                                            value: value || null
                                        }
                                    });
                                }}
                                invalid={!!errors.crsNumber}
                            />
                            <FormFeedback>{errors.crsNumber}</FormFeedback>
                        </FormGroup>
                    </Col>
                    <Col md={4}>
                        <FormGroup>
                            <Label for="taxNumber">
                                Vergi Numarası {taxNumberAccess.required && <span className="text-danger">*</span>}
                            </Label>
                            <InputGroup>
                                <Input
                                    type="text"
                                    id="taxNumber"
                                    name="taxNumber"
                                    value={formData.taxNumber || ''}
                                    disabled={!taxNumberAccess.editable}
                                    onChange={(e) => {
                                        const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
                                        handleInputChange({
                                            target: {
                                                name: 'taxNumber',
                                                value: value || null
                                            }
                                        });
                                    }}
                                    maxLength={10}
                                    invalid={!!errors.taxNumber || (formData.taxNumber && formData.taxNumber.length !== 10)}
                                    placeholder="10 Haneli Vergi No"
                                />
                                <Button
                                    color="primary"
                                    outline
                                    onClick={() => handleCustomerSearch('taxNumber', formData.taxNumber)}
                                    disabled={!taxNumberAccess.editable || isSearchDisabled('taxNumber')}
                                    aria-label="Vergi Numarası ile Müşteri Ara"
                                >
                                    <Search size={16} />
                                </Button>
                                <FormFeedback style={{ display: errors.taxNumber || (formData.taxNumber && formData.taxNumber.length !== 10) ? 'block' : 'none' }}>
                                    {errors.taxNumber || (formData.taxNumber && formData.taxNumber.length !== 10 ? 'Vergi Numarası 10 haneli olmalıdır' : '')}
                                </FormFeedback>
                            </InputGroup>
                        </FormGroup>
                    </Col>
                </Row>
            )}

            {/* --- Added Reference Field --- */}
            <FormGroup>
                <Label for="reference">Kimin Referansı? {referenceAccess.required && <span className="text-danger">*</span>}</Label>
                <Input
                    id="reference"
                    name="reference"
                    value={formData.reference || ''}
                    onChange={handleInputChange}
                    placeholder="Örn: Ahmet Yılmaz (Opsiyonel)"
                    disabled={!referenceAccess.editable}
                    required={referenceAccess.required}
                    invalid={!!errors.reference}
                />
                <FormFeedback>{errors.reference}</FormFeedback>
            </FormGroup>
            {/* --- End Added Reference Field --- */}

            {['Evrak Bekleniyor', 'Eksper İşlem Bekleniyor', 'Eksper Rapor Bekleniyor', 'Sigorta Başvuru Dilekçe Bekleniyor'].includes(callFormState) && (
                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="licensePlate">
                                    Plaka {licensePlateAccess.required && <span className="text-danger">*</span>}
                                </Label>
                                <Input
                                    type="text"
                                    id="licensePlate"
                                    name="licensePlate"
                                    value={formData.licensePlate || ''}
                                    disabled={!licensePlateAccess.editable}
                                    onChange={(e) => {
                                        const value = e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
                                        handleInputChange({
                                            target: {
                                                name: 'licensePlate',
                                                value: value || null
                                            }
                                        });
                                    }}
                                    invalid={!!errors.licensePlate}
                                />
                                <FormFeedback>{errors.licensePlate}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="accidentDate">
                                    Kaza Tarihi {accidentDateAccess.required && <span className="text-danger">*</span>}
                                </Label>
                                <Input
                                    type="datetime-local"
                                    id="accidentDate"
                                    name="accidentDate"
                                    value={formData.accidentDate instanceof Date && !isNaN(formData.accidentDate) 
                                        ? formData.accidentDate.toISOString().slice(0, 16) 
                                        : ''}
                                    disabled={!accidentDateAccess.editable}
                                    onChange={(e) => {
                                        const value = e.target.value ? new Date(e.target.value) : null;
                                        handleInputChange({
                                            target: {
                                                name: 'accidentDate',
                                                value
                                            }
                                        });
                                    }}
                                    invalid={!!errors.accidentDate}
                                />
                                <FormFeedback>{errors.accidentDate}</FormFeedback>
                            </FormGroup>
                        </Col>                                    </Row>
                )}
        </>
    );
};

CustomerInfoForm.propTypes = {
    callFormState: PropTypes.string.isRequired,
    formData: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired,
    handleInputChange: PropTypes.func.isRequired,
    handleCustomerSearch: PropTypes.func.isRequired,
    customerId: PropTypes.string,
    fieldPermissions: PropTypes.array,
    canEdit: PropTypes.bool,
};

export default CustomerInfoForm;
